import { useEffect, useRef, type RefObject } from 'react'

interface UseSpaceHotkeyOptions {
  /** Whether the shortcut is live. Pass `false` while a modal owns the keyboard. */
  enabled: boolean
  onPress: () => void
  /**
   * The control this shortcut mirrors. A focused `<button>` already turns Space
   * into a click, so its own presses are skipped — otherwise one keypress would
   * fire the action twice.
   */
  triggerRef?: RefObject<HTMLElement | null>
}

/** Whether typing a space into this element is what the user meant. */
function isTextEntry(element: EventTarget | null): boolean {
  if (!(element instanceof HTMLElement)) return false
  if (element.isContentEditable) return true
  const tagName = element.tagName
  return tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT'
}

/**
 * Makes the space bar press a button from anywhere on the page, without stealing
 * it from anyone actually typing.
 *
 * The listener sits on `document` so the shortcut works without tabbing to the
 * control first, which is the whole point of having one.
 */
export function useSpaceHotkey({ enabled, onPress, triggerRef }: UseSpaceHotkeyOptions): void {
  // Held in a ref so a new inline callback each render does not re-subscribe.
  const onPressRef = useRef(onPress)
  onPressRef.current = onPress

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== ' ' && event.code !== 'Space') return
      // Holding the bar down would machine-gun the socket.
      if (event.repeat) return
      // Leaves Ctrl/Cmd/Alt+Space to the OS and the browser (Spotlight, IME switching).
      if (event.ctrlKey || event.metaKey || event.altKey) return
      if (isTextEntry(event.target)) return
      if (triggerRef?.current && event.target === triggerRef.current) return

      // Space scrolls the page by default, which is jarring under a button press.
      event.preventDefault()
      onPressRef.current()
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [enabled, triggerRef])
}
